import { platformLabel } from '../../lib/platforms';
import { domainOf } from '../../lib/url';
import type { CampaignRead, PlannedPostRead } from '../../types/api';
import { describeAutomation, statusLabel } from './automation';

/**
 * Drag payload type. A private MIME rather than `text/plain` alone, so a day
 * cell can tell a chip from a paragraph of text dragged in off another page.
 * `text/plain` is set as well because some platforms refuse a drag that carries
 * nothing they recognise.
 */
export const PLANNED_POST_DRAG_TYPE = 'application/x-outpost-planned-post';

interface EntryChipProps {
  post: PlannedPostRead;
  /** The campaign this post belongs to, when it has one. Supplies the colour. */
  campaign: CampaignRead | null;
  onOpen: (post: PlannedPostRead) => void;
}

/**
 * One planned post on a day. Truncated title, the platform when the user has
 * picked one, and the campaign colour as a left edge — the only place a campaign
 * shows up until someone opens the manager.
 *
 * An automated plan carries one more mark: a small glyph for where it is
 * (scheduled, generating, waiting for review, posted, failed). The words are
 * in the title attribute and in the list view; a month cell has room for a
 * glyph and not a sentence.
 *
 * Draggable to another day; the keyboard route to the same move is the date
 * field in the modal this chip opens. A plan the worker holds is not draggable
 * — its day is frozen with the rest of its slot.
 *
 * A post planned off a news article shows its domain here as plain text, not a
 * link: the whole chip is one draggable button, and an anchor cannot live
 * inside it. The link out is in the modal this chip opens, and in the list view.
 */
export function EntryChip({ post, campaign, onOpen }: EntryChipProps) {
  const domain = domainOf(post.source_url);
  const automation = describeAutomation(post);
  const title = [post.title, campaign?.name, domain, automation].filter(Boolean).join(' · ');
  const frozen = post.automation !== 'none' && post.automation_status !== 'idle';
  const status =
    post.automation === 'none' ? null : (statusLabel(post.automation_status) ?? 'Scheduled');

  const classes = ['entry-chip'];
  if (campaign) classes.push('entry-chip--campaign');
  if (post.automation_status === 'needs_review') classes.push('entry-chip--review');
  if (post.automation_status === 'failed') classes.push('entry-chip--failed');

  return (
    <button
      className={classes.join(' ')}
      type="button"
      draggable={!frozen}
      style={campaign ? { borderLeftColor: campaign.color } : undefined}
      title={title}
      onDragStart={(event) => {
        if (frozen) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(PLANNED_POST_DRAG_TYPE, post.id);
        event.dataTransfer.setData('text/plain', post.title);
      }}
      onClick={() => onOpen(post)}
    >
      {status ? (
        <span
          className={`entry-chip__glyph entry-chip__glyph--${post.automation_status}`}
          aria-label={status}
        />
      ) : null}
      <span className="entry-chip__title">{post.title}</span>
      {domain ? <span className="entry-chip__tag entry-chip__tag--source">{domain}</span> : null}
      {post.platform ? (
        <span className="entry-chip__tag">{platformLabel(post.platform)}</span>
      ) : null}
    </button>
  );
}
